import { useCallback, useMemo } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

type QueryDefaults = Record<string, string>;

/**
 * Sync a map of query params with the URL. Values equal to `defaults[key]`
 * are stripped from the URL. Setter preserves other params (e.g. `?open=`).
 */
export function useQueryState<T extends QueryDefaults>(
  defaults: T,
): [T, (patch: Partial<T>) => void] {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const values = useMemo(() => {
    const out = { ...defaults };
    for (const key of Object.keys(defaults)) {
      const v = searchParams.get(key);
      if (v !== null) (out as Record<string, string>)[key] = v;
    }
    return out as T;
  }, [searchParams, defaults]);

  const setValues = useCallback(
    (patch: Partial<T>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined) continue;
        if (value === defaults[key] || value === "") next.delete(key);
        else next.set(key, String(value));
      }
      const qs = next.toString();
      if (qs === searchParams.toString()) return;
      router.replace(qs ? `${pathname}?${qs}` : pathname);
    },
    [router, pathname, searchParams, defaults],
  );

  return [values, setValues];
}
